/**
 * Mirroring an estimate's changes onto the other estimates on the same job.
 *
 * The rules — what matches what, what is copied, what is asked about and what
 * is left alone — are in `packages/core/src/mirror.ts`, where they are tested.
 * This is the part that knows which table each bucket lives in, since the four
 * spell the same four facts differently:
 *
 *   line items          estimate_items.description / customer_price
 *   material order      material_order_items.name / unit_price
 *   work order          work_order_items.description / unit_price
 *   other expenses      job_expenses.name / amount, and no quantity at all
 *
 * The orders are the awkward one: a line on option A's material order has to
 * land on option B's *own* material order, and option B may not have one.
 */

import {
  mirrorKey,
  planMirrorForTarget,
  rateTimesQty,
  toCents,
  toDecimal,
  toUnitRate,
  type Cents,
  type MirrorAction,
  type MirrorBucket,
  type MirrorCandidate,
  type MirrorChange,
  type MirrorRow,
  type MirrorValues,
} from "@roof-hawk/core";

import { deleteRow, insertRow, updateRow } from "../../lib/queries";
import type { fetchEstimateChildren } from "../../lib/queries";

export type EstimateChildren = NonNullable<
  Awaited<ReturnType<typeof fetchEstimateChildren>>
>;

/** A row as it came out of the database, columns and all. */
type Raw = Record<string, unknown>;

const str = (value: unknown): string =>
  typeof value === "string" ? value : "";

const num = (value: unknown): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * What a bucket's rows look like, in the shape core reasons about.
 *
 * A line item's price is its `unit_price` when it has one, not its
 * `customer_price`: `customer_price` is the line *total*, so it moves whenever
 * the quantity does, and mirroring a quantity would otherwise report a price
 * conflict on every estimate priced at a different size. Rows the prototype
 * created have no `unit_price` and fall back to the total, which is the best
 * answer available for them.
 */
function toMirrorRow(bucket: MirrorBucket, raw: Raw): MirrorRow {
  switch (bucket) {
    case "items":
      return {
        id: String(raw.id),
        productId: (raw.product_id as string | null) ?? null,
        label: str(raw.description),
        qty: num(raw.qty),
        price: toCents(raw.unit_price ?? raw.customer_price),
        unit: (raw.unit as string | null) ?? null,
      };
    case "material":
      return {
        id: String(raw.id),
        productId: (raw.product_id as string | null) ?? null,
        label: str(raw.name),
        qty: num(raw.qty),
        price: toCents(raw.unit_price),
        unit: (raw.unit as string | null) ?? null,
      };
    case "work":
      return {
        id: String(raw.id),
        productId: (raw.product_id as string | null) ?? null,
        label: str(raw.description),
        qty: num(raw.qty),
        price: toCents(raw.unit_price),
        unit: (raw.unit as string | null) ?? null,
      };
    case "expenses":
      return {
        id: String(raw.id),
        productId: null,
        label: str(raw.name),
        // An expense is one amount. Fixing the quantity at 1 keeps it out of
        // the diff entirely rather than reporting a change nobody made.
        qty: 1,
        price: toCents(raw.amount),
        unit: null,
      };
  }
}

/** Where a bucket's rows are read from and written to. */
const TABLE: Record<MirrorBucket, string> = {
  items: "estimate_items",
  material: "material_order_items",
  work: "work_order_items",
  expenses: "job_expenses",
};

/**
 * Columns that belong to the row's old home rather than to the line itself.
 *
 * Everything else is copied, so a colour, a scope of work or a tracking number
 * travels with the line rather than being quietly dropped the first time
 * somebody adds a column here and forgets this file.
 */
const NOT_COPIED: Record<MirrorBucket, readonly string[]> = {
  items: [
    "id",
    "estimate_id",
    "created_at",
    "created_by",
    // Whether the work is done is a fact about that estimate's job, not about
    // the line, and a copy that arrives already ticked is a lie.
    "work_completed",
  ],
  material: ["id", "material_order_id", "created_at", "created_by"],
  work: ["id", "work_order_id", "created_at", "created_by"],
  expenses: [
    "id",
    "job_id",
    "estimate_id",
    "created_at",
    "created_by",
    // A receipt belongs to the expense that was actually paid.
    "document_url",
    "tracking_number",
  ],
};

/** The rows of one bucket on one estimate, newest sort order last. */
export function bucketRows(
  bucket: MirrorBucket,
  children: EstimateChildren,
  containerId: string | null,
): Raw[] {
  switch (bucket) {
    case "items":
      return children.items as unknown as Raw[];
    case "expenses":
      return children.expenses as unknown as Raw[];
    case "material": {
      const order = children.materialOrders.find((o) => o.id === containerId);
      return ((order?.items ?? []) as unknown as Raw[]) ?? [];
    }
    case "work": {
      const order = children.workOrders.find((o) => o.id === containerId);
      return ((order?.items ?? []) as unknown as Raw[]) ?? [];
    }
  }
}

export const mirrorRowsOf = (
  bucket: MirrorBucket,
  children: EstimateChildren,
  containerId: string | null,
): MirrorRow[] =>
  bucketRows(bucket, children, containerId).map((raw) =>
    toMirrorRow(bucket, raw),
  );

/**
 * Which order on the target estimate a mirrored order line should land on.
 *
 * The same supplier, or the same crew — that is what makes two orders the same
 * order priced two ways. Failing that, the estimate's only order of that kind,
 * because "one each" is what almost every job has. Two orders and no match is
 * genuinely ambiguous and says so rather than guessing.
 */
function targetContainer(
  bucket: MirrorBucket,
  source: EstimateChildren,
  sourceContainerId: string | null,
  target: EstimateChildren,
): { id: string | null; blocked: string | null } {
  if (bucket === "items" || bucket === "expenses") {
    return { id: null, blocked: null };
  }

  const kind = bucket === "material" ? "material order" : "work order";
  const orders = (bucket === "material"
    ? target.materialOrders
    : target.workOrders) as unknown as Raw[];

  if (orders.length === 0) return { id: null, blocked: `no ${kind}` };

  const from = (
    (bucket === "material"
      ? source.materialOrders
      : source.workOrders) as unknown as Raw[]
  ).find((o) => String(o.id) === sourceContainerId);
  const field = bucket === "material" ? "supplier_id" : "crew_id";
  const wanted = from?.[field] ?? null;

  const matched = wanted
    ? orders.filter((o) => o[field] === wanted)
    : ([] as Raw[]);
  if (matched.length === 1)
    return { id: String(matched[0]!.id), blocked: null };
  if (orders.length === 1) return { id: String(orders[0]!.id), blocked: null };

  return { id: null, blocked: `more than one ${kind}` };
}

/** One other estimate, and everything the prompt needs to say about it. */
export interface MirrorPlan {
  candidate: MirrorCandidate;
  /** Why nothing can be written here, in words. Null when it can. */
  blocked: string | null;
  /** The order the lines would land on, for the two order buckets. */
  containerId: string | null;
  actions: MirrorAction[];
  children: EstimateChildren;
}

export function buildMirrorPlans({
  bucket,
  changes,
  candidates,
  source,
  sourceContainerId,
  childrenById,
}: {
  bucket: MirrorBucket;
  changes: readonly MirrorChange[];
  candidates: readonly MirrorCandidate[];
  source: EstimateChildren;
  sourceContainerId: string | null;
  childrenById: ReadonlyMap<string, EstimateChildren>;
}): MirrorPlan[] {
  return candidates.flatMap((candidate) => {
    const children = childrenById.get(candidate.estimate.id);
    if (!children) return [];

    const container = targetContainer(
      bucket,
      source,
      sourceContainerId,
      children,
    );
    const blocked = candidate.eligible
      ? container.blocked
      : (candidate.reason ?? "not available");

    return [
      {
        candidate,
        blocked,
        containerId: container.id,
        actions: blocked
          ? []
          : planMirrorForTarget(
              changes,
              mirrorRowsOf(bucket, children, container.id),
              candidate.label,
            ),
        children,
      },
    ];
  });
}

/** Where the next row goes, so a copied line lands at the bottom. */
const nextSort = (rows: readonly Raw[]): number =>
  rows.reduce((max, row) => Math.max(max, num(row.sort_order)), 0) + 10;

/**
 * The columns an insert writes: the source line, rehomed.
 *
 * The trade comes from the estimate the line is arriving on, not the one it
 * left — reference values are stored as free text on documents (rule 8) and an
 * estimate's trade trump is what its other lines carry.
 */
function insertValues({
  bucket,
  sourceRaw,
  target,
  targetRows,
}: {
  bucket: MirrorBucket;
  sourceRaw: Raw;
  target: {
    estimateId: string;
    jobId: string;
    trade: string | null;
    containerId: string | null;
  };
  targetRows: readonly Raw[];
}): Raw {
  const values: Raw = {};
  for (const [key, value] of Object.entries(sourceRaw)) {
    if (NOT_COPIED[bucket].includes(key)) continue;
    // Embedded relations from the select (`supplier:suppliers(name)`) are not
    // columns and PostgREST refuses an insert carrying them.
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      continue;
    }
    values[key] = value;
  }
  values.sort_order = nextSort(targetRows);
  if ("trade" in values) values.trade = target.trade ?? values.trade;

  switch (bucket) {
    case "items":
      values.estimate_id = target.estimateId;
      values.work_completed = false;
      break;
    case "expenses":
      values.job_id = target.jobId;
      values.estimate_id = target.estimateId;
      break;
    case "material":
      values.material_order_id = target.containerId;
      break;
    case "work":
      values.work_order_id = target.containerId;
      break;
  }
  return values;
}

/** The columns an update writes, from the fields core decided to carry. */
function updateValues(
  bucket: MirrorBucket,
  values: MirrorValues,
  targetRaw: Raw,
): Raw {
  const patch: Raw = {};
  const qty = values.qty as number | undefined;
  const price = values.price as Cents | undefined;

  if (values.unit !== undefined && bucket !== "expenses") {
    patch.unit = values.unit;
  }
  if (values.productId !== undefined && bucket !== "expenses") {
    patch.product_id = values.productId;
  }
  if (qty !== undefined && bucket !== "expenses") patch.qty = qty;

  switch (bucket) {
    case "items": {
      if (values.label !== undefined) patch.description = values.label;
      // `customer_price` is the line total. When the row prices per unit, the
      // total is the unit price times the quantity — the same helper the line
      // editor calls — so a mirrored quantity moves the total with it. The
      // rate keeps its five decimals: rounding it to the cent first put a
      // mirrored total dollars off the editor's on high-count lines.
      const rate =
        price !== undefined
          ? toUnitRate(toDecimal(price))
          : targetRaw.unit_price != null
            ? toUnitRate(targetRaw.unit_price)
            : null;
      if (price !== undefined) patch.unit_price = toDecimal(price);
      if (rate !== null) {
        const finalQty = qty ?? num(targetRaw.qty);
        patch.customer_price = toDecimal(rateTimesQty(rate, finalQty));
      } else if (price !== undefined) {
        patch.customer_price = toDecimal(price);
      }
      break;
    }
    case "material":
      if (values.label !== undefined) patch.name = values.label;
      if (price !== undefined) patch.unit_price = toDecimal(price);
      break;
    case "work":
      if (values.label !== undefined) patch.description = values.label;
      if (price !== undefined) patch.unit_price = toDecimal(price);
      break;
    case "expenses":
      if (values.label !== undefined) patch.name = values.label;
      if (price !== undefined) patch.amount = toDecimal(price);
      break;
  }
  return patch;
}

/** A quantity the person ticked "change it too" on, keyed plan-then-change. */
export const askKey = (estimateId: string, changeIndex: number): string =>
  `${estimateId}|${changeIndex}`;

/**
 * Write one estimate's share of the mirror.
 *
 * Returns how many rows it touched, which is what the confirmation says. Runs
 * one row at a time on purpose: a plan is a handful of lines, and a failure
 * halfway through leaves the estimate readable rather than half-written by a
 * batch nobody can inspect.
 */
export async function applyMirrorPlan({
  bucket,
  plan,
  sourceRawById,
  jobId,
  asks,
}: {
  bucket: MirrorBucket;
  plan: MirrorPlan;
  sourceRawById: ReadonlyMap<string, Raw>;
  jobId: string;
  /** Which of the quantity questions were answered yes. */
  asks: ReadonlySet<string>;
}): Promise<number> {
  if (plan.blocked) return 0;

  const table = TABLE[bucket];
  const targetRows = bucketRows(bucket, plan.children, plan.containerId);
  const rawById = new Map(targetRows.map((raw) => [String(raw.id), raw]));
  // Copies land at the bottom, one after another, rather than all on the same
  // sort order.
  const placed: Raw[] = [...targetRows];
  let touched = 0;

  for (const [index, action] of plan.actions.entries()) {
    const answeredYes = asks.has(askKey(plan.candidate.estimate.id, index));
    const targetRaw = action.targetRowId
      ? rawById.get(action.targetRowId)
      : undefined;

    if (action.operation === "insert") {
      const sourceRaw =
        action.change.kind === "added"
          ? sourceRawById.get(action.change.row.id)
          : undefined;
      if (!sourceRaw) continue;
      const created = await insertRow<Raw>(
        table,
        insertValues({
          bucket,
          sourceRaw,
          target: {
            estimateId: plan.candidate.estimate.id,
            jobId,
            trade: plan.candidate.estimate.trade ?? null,
            containerId: plan.containerId,
          },
          targetRows: placed,
        }),
      );
      placed.push(created);
      touched += 1;
      continue;
    }

    if (action.operation === "delete" && action.targetRowId) {
      await deleteRow(table, action.targetRowId);
      touched += 1;
      continue;
    }

    // An update, a ticked quantity question, or both.
    const values: MirrorValues = { ...action.values };
    if (answeredYes) {
      for (const ask of action.ask) values[ask.field] = ask.mine;
    }
    if (Object.keys(values).length === 0 || !action.targetRowId) continue;

    const patch = updateValues(bucket, values, targetRaw ?? {});
    if (Object.keys(patch).length === 0) continue;
    await updateRow(table, action.targetRowId, patch);
    touched += 1;
  }

  return touched;
}

/** Every row of a bucket, by id, so an insert can copy the whole line. */
export const rawById = (
  bucket: MirrorBucket,
  children: EstimateChildren,
  containerId: string | null,
): Map<string, Raw> =>
  new Map(
    bucketRows(bucket, children, containerId).map((raw) => [
      String(raw.id),
      raw,
    ]),
  );

export { mirrorKey };
